/**
 * E-Mail-Einrichtung je Queue (Spec 6, Phase 2).
 *
 * Vier Abschnitte, in der Reihenfolge, in der man sie beim Einrichten braucht:
 * die **mandantenweiten Vorgaben** (Standard-Verbindung + Standard-Absender),
 * Empfang (SMTP-Adressen **oder** IMAP-Postfach), Versand (Absender,
 * Reply-Domain) samt DNS-Checkliste, und darunter die Kontrolle — was kam an
 * (`email_inbound_log`) und was ist liegengeblieben (fehlgeschlagene Jobs).
 *
 * **Hier werden keine Zugangsdaten mehr eingetippt** (ADR 0017). Provider,
 * API-Keys und IMAP-Passwoerter liegen in Verbindungen, die der Betreiber
 * unter „Plattform → Verbindungen" pflegt und je Mandant freigibt; diese Seite
 * waehlt nur noch aus den freigegebenen aus.
 *
 * Die Vererbung ist der Punkt: laesst eine Queue ihre Versand-Verbindung auf
 * „Standard des Mandanten", erbt sie die Vorgabe von oben. Eine neue
 * E-Mail-Queue kann damit sofort verschicken, ohne dass jemand etwas eintraegt.
 */
import {
  EMAIL_VERDICTS,
  IMAP_POLL_INTERVAL_DEFAULT_SECONDS,
  SEND_AS_MODES,
  fromAddressMatchesDomains,
  type ConnectionRef,
  type EmailInboundLogEntry,
  type EmailLearnKind,
  type EmailVerdict,
  type FailedJob,
  type InboundConfig,
  type OutboundConfig,
  type Queue,
  type SendAsMode,
  type Tenant,
} from '@tickethub/shared';
import { PlugZap } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { toastError } from '@/lib/errors';
import { useSession } from '@/lib/session';
import { connectionsApi, emailLogApi, jobsApi, queuesApi, tenantApi } from '@/lib/tickets';
import { toast } from 'sonner';

type InboundChannel = 'none' | 'smtp' | 'imap';

type InboundForm = {
  channel: InboundChannel;
  addresses: string;
  /** Referenz auf eine `imap_account`-Verbindung; '' = keine gewaehlt. */
  connectionId: string;
  folder: string;
  processedFolder: string;
  pollInterval: string;
};

type OutboundForm = {
  enabled: boolean;
  /** '' = keine eigene Wahl, es gilt die Standard-Verbindung des Mandanten. */
  connectionId: string;
  fromAddress: string;
  fromName: string;
  sendAs: SendAsMode;
  replyDomain: string;
};

const EMPTY_INBOUND: InboundForm = {
  channel: 'none',
  addresses: '',
  connectionId: '',
  folder: 'INBOX',
  processedFolder: '',
  pollInterval: String(IMAP_POLL_INTERVAL_DEFAULT_SECONDS),
};

const EMPTY_OUTBOUND: OutboundForm = {
  enabled: false,
  connectionId: '',
  fromAddress: '',
  fromName: '',
  sendAs: 'queue',
  replyDomain: '',
};

function inboundFormOf(queue: Queue | null): InboundForm {
  const config = queue?.inbound_config;
  if (!config) return EMPTY_INBOUND;
  if (config.channel === 'smtp') {
    return { ...EMPTY_INBOUND, channel: 'smtp', addresses: config.addresses.join('\n') };
  }
  return {
    ...EMPTY_INBOUND,
    channel: 'imap',
    connectionId: queue?.inbound_connection_id ?? '',
    folder: config.folder,
    processedFolder: config.processed_folder ?? '',
    pollInterval: String(config.poll_interval_seconds),
  };
}

function outboundFormOf(queue: Queue | null): OutboundForm {
  const config = queue?.outbound_config;
  if (!config) return EMPTY_OUTBOUND;
  return {
    enabled: true,
    connectionId: queue?.outbound_connection_id ?? '',
    fromAddress: config.from_address,
    fromName: config.from_name,
    sendAs: config.send_as,
    replyDomain: config.reply_domain,
  };
}

function toInboundInput(form: InboundForm): InboundConfig | null {
  if (form.channel === 'none') return null;
  if (form.channel === 'smtp') {
    return {
      channel: 'smtp',
      addresses: form.addresses
        .split(/[\n,;]/)
        .map((value) => value.trim().toLowerCase())
        .filter((value) => value.length > 0),
    };
  }
  return {
    channel: 'imap',
    folder: form.folder.trim(),
    processed_folder: form.processedFolder.trim().length > 0 ? form.processedFolder.trim() : null,
    poll_interval_seconds: Number(form.pollInterval),
  };
}

function toOutboundInput(form: OutboundForm): OutboundConfig | null {
  if (!form.enabled) return null;
  return {
    from_address: form.fromAddress.trim().toLowerCase(),
    from_name: form.fromName.trim(),
    send_as: form.sendAs,
    reply_domain: form.replyDomain.trim().toLowerCase(),
  };
}

/**
 * Urteile, hinter denen eine einbehaltene Mail steckt (Spec 6.1.1 / 6.3) —
 * nur bei ihnen gibt es „Trotzdem verarbeiten". Muss zu `HELD_VERDICTS` in
 * `services/email-quarantine.ts` passen.
 */
const HELD_VERDICTS: readonly EmailVerdict[] = ['spam', 'rejected'];

const VERDICT_VARIANT: Record<EmailVerdict, 'secondary' | 'outline' | 'destructive'> = {
  accepted: 'secondary',
  duplicate: 'outline',
  loop: 'outline',
  bounced: 'destructive',
  rejected: 'destructive',
  spam: 'destructive',
};

export function MailPage() {
  const { t, i18n } = useTranslation();
  const { activeMembership } = useSession();
  const tenantId = activeMembership?.tenant_id ?? null;

  const [queues, setQueues] = useState<Queue[] | null>(null);
  const [selectedId, setSelectedId] = useState('');
  const [inbound, setInbound] = useState<InboundForm>(EMPTY_INBOUND);
  const [outbound, setOutbound] = useState<OutboundForm>(EMPTY_OUTBOUND);
  const [busy, setBusy] = useState(false);

  const [logEntries, setLogEntries] = useState<EmailInboundLogEntry[]>([]);
  const [verdict, setVerdict] = useState<EmailVerdict | ''>('');
  const [failedJobs, setFailedJobs] = useState<FailedJob[]>([]);

  // Verbindungs-Vault (ADR 0017): nur die **freigegebenen** stehen zur Wahl.
  const [connections, setConnections] = useState<ConnectionRef[]>([]);
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [defaults, setDefaults] = useState({ connectionId: '', fromAddress: '', fromName: '' });

  const selected = queues?.find((queue) => queue.id === selectedId) ?? null;
  const formatter = new Intl.DateTimeFormat(i18n.language, {
    dateStyle: 'short',
    timeStyle: 'short',
  });

  const loadQueues = useCallback(async () => {
    if (!tenantId) return;
    try {
      const result = await queuesApi.list(tenantId);
      setQueues(result.items);
      setSelectedId((current) => current || (result.items[0]?.id ?? ''));
    } catch (error) {
      setQueues([]);
      toastError(error, t);
    }
  }, [tenantId, t]);

  const loadConnections = useCallback(async () => {
    if (!tenantId) return;
    try {
      const [list, current] = await Promise.all([
        connectionsApi.list(tenantId),
        tenantApi.get(tenantId),
      ]);
      setConnections(list.items);
      setTenant(current);
      setDefaults({
        connectionId: current.default_mail_connection_id ?? '',
        fromAddress: current.default_from_address ?? '',
        fromName: current.default_from_name ?? '',
      });
    } catch (error) {
      toastError(error, t);
    }
  }, [tenantId, t]);

  const loadLog = useCallback(async () => {
    if (!tenantId) return;
    try {
      const result = await emailLogApi.list(tenantId, verdict ? { verdict } : {});
      setLogEntries(result.items);
    } catch (error) {
      toastError(error, t);
    }
  }, [tenantId, verdict, t]);

  const loadJobs = useCallback(async () => {
    if (!tenantId) return;
    try {
      const result = await jobsApi.failed(tenantId);
      setFailedJobs(result.items);
    } catch (error) {
      toastError(error, t);
    }
  }, [tenantId, t]);

  /** Retry/Loeschen aus der Job-Ansicht — danach frisch laden. */
  async function runJobAction(action: () => Promise<unknown>) {
    try {
      await action();
      await loadJobs();
    } catch (error) {
      toastError(error, t);
    }
  }

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Daten beim Mount laden
    void loadQueues();
  }, [loadQueues]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Daten beim Mount laden
    void loadConnections();
  }, [loadConnections]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Filterwechsel laedt neu
    void loadLog();
  }, [loadLog]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Daten beim Mount laden
    void loadJobs();
  }, [loadJobs]);

  useEffect(() => {
    // Die Formulare folgen der Auswahl; Geheimnisse bleiben dabei leer.
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Auswahl spiegeln
    setInbound(inboundFormOf(selected));
    setOutbound(outboundFormOf(selected));
  }, [selected]);

  async function save() {
    if (!tenantId || !selected) return;
    setBusy(true);
    try {
      await queuesApi.saveEmail(tenantId, selected.id, {
        inbound_config: toInboundInput(inbound),
        outbound_config: toOutboundInput(outbound),
        // '' heisst „keine eigene Wahl" — der Mandanten-Default gilt.
        outbound_connection_id: outbound.connectionId === '' ? null : outbound.connectionId,
        inbound_connection_id: inbound.connectionId === '' ? null : inbound.connectionId,
      });
      toast.success(t('mail.saved'));
      await loadQueues();
    } catch (error) {
      toastError(error, t);
    } finally {
      setBusy(false);
    }
  }

  /** Mandantenweite Vorgaben speichern — die Quelle der Vererbung. */
  async function saveDefaults() {
    if (!tenantId) return;
    setBusy(true);
    try {
      await tenantApi.update(tenantId, {
        default_mail_connection_id: defaults.connectionId === '' ? null : defaults.connectionId,
        default_from_address:
          defaults.fromAddress.trim() === '' ? null : defaults.fromAddress.trim().toLowerCase(),
        default_from_name: defaults.fromName.trim() === '' ? null : defaults.fromName.trim(),
      });
      toast.success(t('mail.saved'));
      await loadConnections();
      await loadQueues();
    } catch (error) {
      toastError(error, t);
    } finally {
      setBusy(false);
    }
  }

  /**
   * Quarantaene freigeben (Spec 6.1.1). Was dabei herauskommt, ist ein ganz
   * normales Pipeline-Urteil — auch „doch ein Duplikat" ist ein Ergebnis.
   */
  async function processEntry(entryId: string) {
    if (!tenantId) return;
    setBusy(true);
    try {
      const result = await emailLogApi.process(tenantId, entryId);
      if (result.ticket_id === null) {
        toast.warning(t(`mail.verdict_${result.verdict}`));
      } else {
        toast.success(t('mail.log_processed'));
      }
      await loadLog();
    } catch (error) {
      toastError(error, t);
    } finally {
      setBusy(false);
    }
  }

  /** Bayes trainieren (Spec 6.1.1). */
  async function learnEntry(entryId: string, kind: EmailLearnKind) {
    if (!tenantId) return;
    setBusy(true);
    try {
      const result = await emailLogApi.learn(tenantId, entryId, kind);
      toast.success(result.learned ? t('mail.log_learned') : t('mail.log_learned_already'));
    } catch (error) {
      toastError(error, t);
    } finally {
      setBusy(false);
    }
  }

  async function testConnection() {
    if (!tenantId || !selected) return;
    setBusy(true);
    try {
      const result = await queuesApi.testEmail(tenantId, selected.id);
      toast.success(t('mail.test_ok', { folder: result.folder, count: result.total }));
    } catch (error) {
      toastError(error, t);
    } finally {
      setBusy(false);
    }
  }

  if (!activeMembership) {
    return (
      <Alert>
        <AlertDescription>{t('tenant.empty')}</AlertDescription>
      </Alert>
    );
  }

  const mailConnections = connections.filter((entry) => entry.type === 'mail_provider');
  const imapConnections = connections.filter((entry) => entry.type === 'imap_account');
  const defaultConnectionName =
    mailConnections.find((entry) => entry.id === (tenant?.default_mail_connection_id ?? ''))?.name ??
    null;

  /**
   * Welche Verbindung dieser Versand **wirklich** benutzt — eigene Wahl oder
   * der geerbte Standard. Grundlage der Absender-Warnung darunter.
   */
  const effectiveConnection =
    mailConnections.find(
      (entry) =>
        entry.id ===
        (outbound.connectionId === ''
          ? (tenant?.default_mail_connection_id ?? '')
          : outbound.connectionId),
    ) ?? null;
  const domainMismatch =
    outbound.enabled &&
    outbound.fromAddress.trim().length > 0 &&
    effectiveConnection !== null &&
    !fromAddressMatchesDomains(outbound.fromAddress.trim(), effectiveConnection.domains);

  return (
    <div className="space-y-8">
      <section className="space-y-3">
        <h1 className="text-2xl font-semibold">{t('mail.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('mail.intro')}</p>

        {queues === null ? (
          <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
        ) : queues.length === 0 ? (
          <Alert>
            <AlertDescription>{t('queue.empty')}</AlertDescription>
          </Alert>
        ) : (
          <div className="space-y-2">
            <Label htmlFor="mail-queue">{t('mail.queue')}</Label>
            <Select
              id="mail-queue"
              value={selectedId}
              onChange={(event) => setSelectedId(event.target.value)}
            >
              {queues.map((queue) => (
                <option key={queue.id} value={queue.id}>
                  {queue.name}
                </option>
              ))}
            </Select>
          </div>
        )}
      </section>

      {/* ── Mandantenweite Vorgaben (ADR 0017) ─────────────────────────── */}
      <section className="space-y-4 rounded-lg border p-4">
        <h2 className="text-lg font-semibold">{t('mail.defaults_title')}</h2>
        <p className="text-sm text-muted-foreground">{t('mail.defaults_intro')}</p>

        {mailConnections.length === 0 ? (
          <Alert>
            <AlertDescription>{t('mail.no_connections')}</AlertDescription>
          </Alert>
        ) : null}

        <div className="grid gap-3 sm:grid-cols-3">
          <div className="space-y-2">
            <Label htmlFor="default-connection">{t('mail.connection')}</Label>
            <Select
              id="default-connection"
              value={defaults.connectionId}
              onChange={(event) =>
                setDefaults({ ...defaults, connectionId: event.target.value })
              }
            >
              <option value="">{t('mail.connection_none')}</option>
              {mailConnections.map((connection) => (
                <option key={connection.id} value={connection.id}>
                  {connection.name}
                </option>
              ))}
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="default-from">{t('mail.from_address')}</Label>
            <Input
              id="default-from"
              type="email"
              value={defaults.fromAddress}
              onChange={(event) => setDefaults({ ...defaults, fromAddress: event.target.value })}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="default-from-name">{t('mail.from_name')}</Label>
            <Input
              id="default-from-name"
              value={defaults.fromName}
              onChange={(event) => setDefaults({ ...defaults, fromName: event.target.value })}
            />
          </div>
        </div>

        <Button disabled={busy} onClick={() => void saveDefaults()}>
          {t('mail.save')}
        </Button>
      </section>

      {selected ? (
        <>
          <section className="space-y-4 rounded-lg border p-4">
            <h2 className="text-lg font-semibold">{t('mail.inbound_title')}</h2>

            <div className="space-y-2">
              <Label htmlFor="inbound-channel">{t('mail.channel')}</Label>
              <Select
                id="inbound-channel"
                value={inbound.channel}
                onChange={(event) =>
                  setInbound({ ...inbound, channel: event.target.value as InboundChannel })
                }
              >
                <option value="none">{t('mail.channel_none')}</option>
                <option value="smtp">{t('mail.channel_smtp')}</option>
                <option value="imap">{t('mail.channel_imap')}</option>
              </Select>
            </div>

            {inbound.channel === 'smtp' ? (
              <div className="space-y-2">
                <Label htmlFor="inbound-addresses">{t('mail.addresses')}</Label>
                <textarea
                  id="inbound-addresses"
                  className="min-h-24 w-full rounded-md border border-input bg-background p-2 text-sm"
                  value={inbound.addresses}
                  onChange={(event) => setInbound({ ...inbound, addresses: event.target.value })}
                />
                <p className="text-xs text-muted-foreground">{t('mail.addresses_hint')}</p>
              </div>
            ) : null}

            {inbound.channel === 'imap' ? (
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="imap-connection">{t('mail.imap_connection')}</Label>
                  <Select
                    id="imap-connection"
                    value={inbound.connectionId}
                    onChange={(event) =>
                      setInbound({ ...inbound, connectionId: event.target.value })
                    }
                  >
                    <option value="">{t('mail.connection_choose')}</option>
                    {imapConnections.map((connection) => (
                      <option key={connection.id} value={connection.id}>
                        {connection.name}
                      </option>
                    ))}
                  </Select>
                  <p className="text-xs text-muted-foreground">{t('mail.imap_connection_hint')}</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="imap-folder">{t('mail.imap_folder')}</Label>
                  <Input
                    id="imap-folder"
                    value={inbound.folder}
                    onChange={(event) => setInbound({ ...inbound, folder: event.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="imap-processed">{t('mail.imap_processed_folder')}</Label>
                  <Input
                    id="imap-processed"
                    value={inbound.processedFolder}
                    onChange={(event) =>
                      setInbound({ ...inbound, processedFolder: event.target.value })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="imap-interval">{t('mail.imap_interval')}</Label>
                  <Input
                    id="imap-interval"
                    inputMode="numeric"
                    value={inbound.pollInterval}
                    onChange={(event) => setInbound({ ...inbound, pollInterval: event.target.value })}
                  />
                </div>
              </div>
            ) : null}

            {inbound.channel === 'imap' && selected.inbound_config?.channel === 'imap' ? (
              <Button variant="outline" size="sm" disabled={busy} onClick={() => void testConnection()}>
                <PlugZap aria-hidden="true" />
                {t('mail.test')}
              </Button>
            ) : null}
          </section>

          <section className="space-y-4 rounded-lg border p-4">
            <h2 className="text-lg font-semibold">{t('mail.outbound_title')}</h2>

            <div className="space-y-1">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={outbound.enabled}
                  onChange={(event) => setOutbound({ ...outbound, enabled: event.target.checked })}
                />
                {t('mail.outbound_enabled')}
              </label>
              {/* Der Schalter wird sonst als globaler Modus gelesen. Die
                  Kanalweiche steckt in `outboundChannelFor()` (mail/send.ts). */}
              <p className="text-xs text-muted-foreground">{t('mail.outbound_enabled_hint')}</p>
            </div>

            {outbound.enabled ? (
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="outbound-connection">{t('mail.connection')}</Label>
                  <Select
                    id="outbound-connection"
                    value={outbound.connectionId}
                    onChange={(event) =>
                      setOutbound({ ...outbound, connectionId: event.target.value })
                    }
                  >
                    <option value="">{t('mail.connection_inherit')}</option>
                    {mailConnections.map((connection) => (
                      <option key={connection.id} value={connection.id}>
                        {connection.name}
                      </option>
                    ))}
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {outbound.connectionId === ''
                      ? t('mail.connection_inherit_hint', {
                          name: defaultConnectionName ?? t('mail.connection_none'),
                        })
                      : t('mail.connection_own_hint')}
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="outbound-from">{t('mail.from_address')}</Label>
                  <Input
                    id="outbound-from"
                    type="email"
                    value={outbound.fromAddress}
                    onChange={(event) =>
                      setOutbound({ ...outbound, fromAddress: event.target.value })
                    }
                  />
                  {/* Nur ein Hinweis, kein Riegel: die Domainliste wird von
                      Hand gepflegt und kann veraltet sein. */}
                  {domainMismatch ? (
                    <p className="text-xs text-destructive">{t('mail.from_domain_mismatch')}</p>
                  ) : null}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="outbound-from-name">{t('mail.from_name')}</Label>
                  <Input
                    id="outbound-from-name"
                    value={outbound.fromName}
                    onChange={(event) => setOutbound({ ...outbound, fromName: event.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="outbound-send-as">{t('mail.send_as')}</Label>
                  <Select
                    id="outbound-send-as"
                    value={outbound.sendAs}
                    onChange={(event) =>
                      setOutbound({ ...outbound, sendAs: event.target.value as SendAsMode })
                    }
                  >
                    {SEND_AS_MODES.map((mode) => (
                      <option key={mode} value={mode}>
                        {t(`mail.send_as_${mode}`)}
                      </option>
                    ))}
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="outbound-reply-domain">{t('mail.reply_domain')}</Label>
                  <Input
                    id="outbound-reply-domain"
                    value={outbound.replyDomain}
                    onChange={(event) =>
                      setOutbound({ ...outbound, replyDomain: event.target.value })
                    }
                  />
                  <p className="text-xs text-muted-foreground">{t('mail.reply_domain_hint')}</p>
                </div>
              </div>
            ) : null}
          </section>

          <Button disabled={busy} onClick={() => void save()}>
            {t('mail.save')}
          </Button>

          <DnsChecklist />
        </>
      ) : null}

      <section className="space-y-3">
        <h2 className="text-lg font-semibold">{t('mail.log_title')}</h2>
        <p className="text-sm text-muted-foreground">{t('mail.log_intro')}</p>

        <div className="space-y-2">
          <Label htmlFor="log-verdict">{t('mail.verdict')}</Label>
          <Select
            id="log-verdict"
            value={verdict}
            onChange={(event) => setVerdict(event.target.value as EmailVerdict | '')}
          >
            <option value="">{t('mail.verdict_all')}</option>
            {EMAIL_VERDICTS.map((value) => (
              <option key={value} value={value}>
                {t(`mail.verdict_${value}`)}
              </option>
            ))}
          </Select>
        </div>

        {logEntries.length === 0 ? (
          <Alert>
            <AlertDescription>{t('mail.log_empty')}</AlertDescription>
          </Alert>
        ) : (
          <ul className="space-y-2">
            {logEntries.map((entry) => (
              <li key={entry.id} className="space-y-2 rounded-lg border p-3 text-sm">
                <div className="flex flex-wrap items-center gap-3">
                  <Badge variant={VERDICT_VARIANT[entry.verdict]}>
                    {t(`mail.verdict_${entry.verdict}`)}
                  </Badge>
                  <span className="font-medium">{entry.subject ?? t('mail.no_subject')}</span>
                  <span className="text-muted-foreground">{entry.from_address}</span>
                  {entry.reason ? (
                    <span className="text-xs text-muted-foreground">({entry.reason})</span>
                  ) : null}
                  {entry.spam_score !== null ? (
                    <span className="text-xs text-muted-foreground">
                      {t('mail.log_score', { score: entry.spam_score.toFixed(1) })}
                    </span>
                  ) : null}
                  <time
                    className="ml-auto text-xs text-muted-foreground"
                    dateTime={entry.created_at}
                  >
                    {formatter.format(new Date(entry.created_at))}
                  </time>
                </div>
                {/*
                  Aktionen nur, solange die Rohmail noch da ist — nach der
                  Retention (Spec 13.4) gibt es nichts mehr zu verarbeiten
                  oder zu lernen.
                */}
                {entry.raw_ref !== null ? (
                  <div className="flex flex-wrap gap-2">
                    {HELD_VERDICTS.includes(entry.verdict) ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={busy}
                        onClick={() => void processEntry(entry.id)}
                      >
                        {t('mail.log_process')}
                      </Button>
                    ) : null}
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      disabled={busy}
                      onClick={() => void learnEntry(entry.id, 'spam')}
                    >
                      {t('mail.log_learn_spam')}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      disabled={busy}
                      onClick={() => void learnEntry(entry.id, 'ham')}
                    >
                      {t('mail.log_learn_ham')}
                    </Button>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="space-y-3">
        <h2 className="text-lg font-semibold">{t('mail.jobs_title')}</h2>
        <p className="text-sm text-muted-foreground">{t('mail.jobs_intro')}</p>

        {failedJobs.length === 0 ? (
          <Alert>
            <AlertDescription>{t('mail.jobs_empty')}</AlertDescription>
          </Alert>
        ) : (
          <ul className="space-y-2">
            {failedJobs.map((job) => (
              <li key={`${job.queue}:${job.id}`} className="space-y-1 rounded-lg border p-3 text-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="destructive">{job.queue}</Badge>
                  <span className="text-muted-foreground">
                    {t('mail.jobs_attempts', { count: job.attempts })}
                  </span>
                  {job.failed_at ? (
                    <time className="ml-auto text-xs text-muted-foreground" dateTime={job.failed_at}>
                      {formatter.format(new Date(job.failed_at))}
                    </time>
                  ) : null}
                </div>
                <p className="break-all text-xs text-muted-foreground">{job.failed_reason}</p>
                {/* Bedienbar seit Phase 5 (CLAUDE.md, Override „DLQ"). */}
                <div className="flex gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      void runJobAction(() => jobsApi.retry(tenantId ?? '', job.queue, job.id))
                    }
                  >
                    {t('mail.jobs_retry')}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      void runJobAction(() => jobsApi.remove(tenantId ?? '', job.queue, job.id))
                    }
                  >
                    {t('mail.jobs_delete')}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

/**
 * Statischer Hinweistext (Spec 6.4). Bewusst keine Live-Pruefung: die DNS-
 * Eintraege macht ohnehin ein Mensch beim Provider, und eine halbherzige
 * Pruefung wuerde nur falsche Sicherheit erzeugen.
 */
function DnsChecklist() {
  const { t } = useTranslation();
  const items = ['spf', 'dkim', 'dmarc', 'mx', 'ptr'] as const;

  return (
    <Alert>
      <AlertTitle>{t('mail.dns_title')}</AlertTitle>
      <AlertDescription>
        <ul className="mt-2 list-disc space-y-1 pl-5 text-sm">
          {items.map((item) => (
            <li key={item}>
              <strong>{t(`mail.dns_${item}_label`)}:</strong> {t(`mail.dns_${item}`)}
            </li>
          ))}
        </ul>
      </AlertDescription>
    </Alert>
  );
}
